import {
  ProjectDecisionWord,
  projectAssetArchiveReasons,
  projectAssetDuplicateFailures,
  projectAssetDuplicateProposalStatuses,
  ProjectAssetDuplicateProposalStatus,
  projectRegisterMergeRefusals,
  projectApplyItemWarnings,
  ProjectRegisterApproveBlocker,
  projectRegisterApproveBlockers,
  ProjectState,
  ProjectRoomAvailability,
  ProjectTier,
  parseProjectDecisionWord,
  parseProjectInquiryBlocker,
  parseProjectInquiryGroupStatus,
  parseProjectInquiryListMode,
  parseProjectInquiryStatus,
  parseProjectMilestoneState,
  parseProjectMilestoneSwitchBlocker,
  parseProjectRoomHolderLicenceState,
  parseProjectTargetFit,
  parseProjectTrackForecastKind,
  parseProjectTrackMessageCode,
  parseProjectTrackMessageTone,
  parseProjectTrackMoveKind,
  parseProjectTrackSegmentKind,
  parseProjectTrackState,
  parseProjectWriteBlocker,
} from 'models/projects/projectEnums';
import {
  KickstartOfferDTO,
  ProjectDTO,
  ProjectExternalAuditDTO,
  ProjectSummaryDTO,
  ProjectStateRequestDTO,
  ProjectTrackPreviewRequestDTO,
  ProjectRoomAvailabilityDTO,
  ProjectMilestoneDTO,
  ProjectStepDTO,
  ProjectTierDTO,
  ProjectTrackDTO,
  ProjectTrackMessageDTO,
  ProjectTrackMoveDTO,
  ProjectTrackSegmentDTO,
  ProjectRoomTodoDTO,
} from 'models/dto/projects/projectDTO';
import {
  ProjectApplyDecisionDTO,
  ProjectApplyItemResultDTO,
  ProjectApplyRequestDTO,
  ProjectApplyResultDTO,
  ProjectRiskPrecheckDTO,
  ProjectRoomDTO,
  ProjectRoomInquirySignalDTO,
  ProjectRoomItemDTO,
  ProjectRoomRelatedDTO,
} from 'models/dto/projects/projectRoomDTO';
import {
  PersonRefDTO,
  ProjectAssetAnswerDTO,
  ProjectAssetDuplicatesDTO,
  ProjectDocumentRowPartDTO,
  ProjectDocumentsRoomPartDTO,
  ProjectInquiryDTO,
  ProjectInquiryListGroupDTO,
  ProjectInquiryProgressRowPartDTO,
  ProjectInquirySendTermsDTO,
  ProjectOutcomeGroupDTO,
  ProjectOwnershipRowPartDTO,
  ProjectQuestionRowPartDTO,
  ProjectRoomAssetListGroupDTO,
  ProjectRoomHolderDTO,
  ProjectRoomInquiryAssetDTO,
  ProjectExternalAuditRoomPartDTO,
  ProjectRoomItemPartDTO,
  ProjectRoomPartDTO,
  ProjectRoomRegisterWaitDTO,
  ProjectRoomUnownedControlDTO,
  ProjectRoomOwnedItemDTO,
} from 'models/dto/projects/projectRoomPartsDTO';
import Project, {
  KickstartOffer,
  ProjectExternalAudit,
  ProjectExternalAuditStep,
  projectExternalAuditMoments,
  projectExternalAuditUndos,
  ProjectMilestone,
  ProjectRoomAvailabilityInfo,
  ProjectRoomProgress,
  ProjectRoomTodo,
  ProjectStep,
  ProjectSummary,
  ProjectTierInfo,
  ProjectTrack,
  ProjectTrackMessage,
  ProjectTrackMove,
  ProjectTrackSegment,
} from 'models/projects/project';
import ProjectRoom, {
  ProjectApplyOutcome,
  projectApplyOutcomes,
  ProjectApplyResult,
  ProjectApplyResultItem,
  ProjectRiskPrecheck,
  ProjectRoomItem,
  ProjectRoomRelated,
  ProjectRoomInquirySignal,
} from 'models/projects/projectRoom';
import {
  PersonRef,
  ProjectAssetDuplicateProposal,
  ProjectAssetDuplicates,
  ProjectAssetLineAnswer,
  ProjectDocumentRowPart,
  ProjectDocumentsRoomPart,
  ProjectInquiry,
  ProjectInquiryListGroup,
  ProjectInquiryProgressRowPart,
  ProjectInquirySendTerms,
  ProjectOutcomeGroup,
  ProjectOwnershipRowPart,
  ProjectQuestionRowPart,
  ProjectRoomAssetListGroup,
  ProjectRoomHolder,
  ProjectRoomInquiryAsset,
  ProjectRoomItemPart,
  ProjectRoomPart,
  ProjectRoomRegisterWait,
  ProjectRoomOwnedItem,
  ProjectOutcomeAnswer,
  ProjectUnownedControl,
  projectDocumentChoices,
  projectKickoffApproveBlocks,
  projectKickoffApproveModes,
  projectUnownedControlReasons,
  projectOutcomeAnswers,
  parseProjectOneOf,
} from 'models/projects/projectRoomParts';
import { RiskMethod } from 'models/risk';
import {
  fromApiDateOptional,
  fromApiDateTimeOptional,
  fromApiDateTimeRequired,
  toApiDateOptional,
} from 'utils/datetime';
import { htmlToText } from 'utils/html';
import { mapFromDashboard } from './dashboardMapping';
import { mapFromAssessmentTaskCreatedDTO } from './auditCriteriaGenericMapping';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';

// A comment as text, for a place that shows it as text (an inquiry's messages): a comment from the editor or the
// server is HTML, whose tags and entities become text; a message typed in a plain field keeps its own line breaks.
const commentToText = (comment: string | null | undefined): string => {
  if (!comment) return '';
  if (!/<[a-z/!][^>]*>|&(#\d+|#x[0-9a-f]+|[a-z]+);/i.test(comment)) return comment;

  return htmlToText(comment, false).trim();
};

//
// Project v2 project aggregate mappers.
//
// The API's Project constants are mirrored here by hand, the same way every other platform enum is
// mirrored under models/ (see models/entity.ts for EntityTypes), so a value that reaches here is
// trusted to be one this mirror already knows. The casts below carry that trust into the type
// system rather than re-validating it at runtime.
//

export function mapFromProjectStep(dto: ProjectStepDTO): ProjectStep {
  const task = new ProjectStep();
  task.taskId = dto.taskId ?? 0;
  task.name = dto.name ?? '';
  task.sortOrder = dto.sortOrder ?? 0;
  task.completed = dto.completed ?? false;
  task.completedDate = fromApiDateTimeOptional(dto.completedDate);
  task.key = dto.key;
  task.started = dto.started ?? false;

  return task;
}

function mapFromProjectExternalAuditRoomPart(dto: ProjectExternalAuditRoomPartDTO): ProjectExternalAuditStep {
  const step = new ProjectExternalAuditStep();
  step.quoteRequestedOn = fromApiDateTimeOptional(dto.quoteRequestedOn ?? undefined);
  step.quoteRequestedBy = mapFromPersonRef(dto.quoteRequestedBy ?? undefined);
  step.requestedThroughIsoplanner = dto.requestedThroughIsoplanner ?? false;
  step.quoteSignedOn = fromApiDateTimeOptional(dto.quoteSignedOn ?? undefined);
  step.quoteSignedBy = mapFromPersonRef(dto.quoteSignedBy ?? undefined);
  step.arrangedElsewhereOn = fromApiDateTimeOptional(dto.arrangedElsewhereOn ?? undefined);
  step.arrangedElsewhereBy = mapFromPersonRef(dto.arrangedElsewhereBy ?? undefined);
  step.stage1On = fromApiDateTimeOptional(dto.stage1On ?? undefined);
  step.stage2On = fromApiDateTimeOptional(dto.stage2On ?? undefined);
  step.skippedOn = fromApiDateTimeOptional(dto.skippedOn ?? undefined);
  step.skippedBy = mapFromPersonRef(dto.skippedBy ?? undefined);
  step.skipReason = dto.skipReason ?? undefined;
  step.done = dto.done ?? false;
  // The server always sends where the room stands; an absent one is a room with nothing passed yet.
  step.moment = projectExternalAuditMoments.find((moment) => moment === dto.moment) ?? 'start';
  step.undo = projectExternalAuditUndos.find((undo) => undo === dto.undo);

  return step;
}

export function mapFromProjectExternalAudit(dto: ProjectExternalAuditDTO): ProjectExternalAudit {
  const externalAudit = new ProjectExternalAudit();
  externalAudit.quoteRequestEnabled = dto.quoteRequestEnabled ?? false;
  externalAudit.meetingUrl = dto.meetingUrl;

  return externalAudit;
}

export function mapFromProjectMilestone(dto: ProjectMilestoneDTO): ProjectMilestone {
  const milestone = new ProjectMilestone();
  milestone.taskId = dto.taskId ?? 0;
  milestone.name = dto.name ?? '';
  milestone.description = dto.description ?? '';
  milestone.completed = dto.completed ?? false;
  milestone.completedDate = fromApiDateTimeOptional(dto.completedDate);
  // A value this client cannot name falls back to `todo`, which is the state that claims nothing:
  // the glyph and the chip both show "not started" rather than inventing progress.
  milestone.state = parseProjectMilestoneState(dto.state) ?? 'todo';
  // These are moments, not dates: task timestamps that the milestone chips state as instants.
  milestone.startDate = fromApiDateTimeOptional(dto.startDate);
  milestone.deadline = fromApiDateTimeOptional(dto.deadline);
  milestone.tasksDone = dto.tasksDone ?? 0;
  milestone.tasksTotal = dto.tasksTotal ?? 0;
  milestone.roomsDone = dto.roomsDone ?? 0;
  milestone.roomsTotal = dto.roomsTotal ?? 0;
  milestone.tasks = (dto.tasks ?? []).map(mapFromProjectStep);
  milestone.switchBlocker = parseProjectMilestoneSwitchBlocker(dto.switchBlocker);
  milestone.closesWhenStepsDone = dto.closesWhenStepsDone ?? false;

  return milestone;
}

function mapFromProjectTrackSegment(dto: ProjectTrackSegmentDTO): ProjectTrackSegment {
  const segment = new ProjectTrackSegment();
  segment.milestoneTaskId = dto.milestoneTaskId;
  // `todo` is the band that draws nothing and claims nothing, so an unknown kind maps to it.
  segment.kind = parseProjectTrackSegmentKind(dto.kind) ?? 'todo';
  segment.state = parseProjectMilestoneState(dto.state) ?? 'todo';
  segment.from = fromApiDateTimeOptional(dto.from);
  segment.to = fromApiDateTimeOptional(dto.to);
  segment.weight = dto.weight ?? 0;

  return segment;
}

/**
 * The track's one sentence. A tone or a code this client cannot name drops the whole message to
 * undefined, since a bar with a tone and no copy would be unreadable.
 */
function mapFromProjectTrackMessage(dto: ProjectTrackMessageDTO): ProjectTrackMessage | undefined {
  const tone = parseProjectTrackMessageTone(dto.tone);
  const code = parseProjectTrackMessageCode(dto.code);
  if (tone === undefined || code === undefined) return undefined;

  const message = new ProjectTrackMessage();
  message.tone = tone;
  message.code = code;

  return message;
}

/**
 * One move. An unknown kind is dropped by the track mapper: a move this client cannot describe is
 * not a move.
 */
function mapFromProjectTrackMove(dto: ProjectTrackMoveDTO): ProjectTrackMove | undefined {
  const kind = parseProjectTrackMoveKind(dto.kind);
  if (kind === undefined) return undefined;

  const move = new ProjectTrackMove();
  move.kind = kind;
  move.milestoneTaskId = dto.milestoneTaskId;
  move.taskId = dto.taskId;
  move.roomKey = dto.roomKey;
  // Absent stays absent: 0 is a real measurement - this move moves the forecast by nothing.
  move.impactDays = dto.impactDays;
  move.primary = dto.primary ?? false;
  move.todo = mapFromProjectRoomTodos(dto.todo);

  return move;
}

/**
 * The whole track.
 *
 * Every date here is a moment except the target, which is a day on a calendar: `today`,
 * `startDate`, `forecastMonth`, `forecastDate` and the segment ends are instants the server
 * computed against, and reading them through the date-only helper would shift them by a time
 * zone. The target is the one the user typed into a picker, so it goes through
 * `fromApiDateOptional`, the same as the project's own field.
 */
export function mapFromProjectTrack(dto: ProjectTrackDTO): ProjectTrack {
  const track = new ProjectTrack();
  track.state = parseProjectTrackState(dto.state) ?? 'notStarted';
  track.doneCount = dto.doneCount ?? 0;
  track.totalCount = dto.totalCount ?? 0;
  track.startDate = fromApiDateTimeOptional(dto.startDate);
  track.today = fromApiDateTimeOptional(dto.today) ?? new Date(0);
  track.targetDate = fromApiDateOptional(dto.targetDate);
  track.forecastKind = parseProjectTrackForecastKind(dto.forecastKind) ?? 'none';
  track.forecastMonth = fromApiDateTimeOptional(dto.forecastMonth);
  track.forecastDate = fromApiDateTimeOptional(dto.forecastDate);
  track.slipDays = dto.slipDays;
  track.targetFit = parseProjectTargetFit(dto.targetFit) ?? 'none';
  track.idleDays = dto.idleDays ?? 0;
  track.segments = (dto.segments ?? []).map(mapFromProjectTrackSegment);
  track.message = dto.message === undefined ? undefined : mapFromProjectTrackMessage(dto.message);
  track.moves = (dto.moves ?? [])
    .map(mapFromProjectTrackMove)
    .filter((move): move is ProjectTrackMove => move !== undefined);
  track.stateHash = dto.stateHash ?? '';

  return track;
}

/**
 * One room's availability row.
 *
 * A row whose key the wire did not carry keeps the empty key and the `notSeeded` state the model
 * constructs with: an unknown room stays explicitly unknown rather than defaulting to a real one,
 * because a mis-keyed row could otherwise be interpreted as naming the wrong room.
 */
export function mapFromProjectRoomAvailability(dto: ProjectRoomAvailabilityDTO): ProjectRoomAvailabilityInfo {
  const room = new ProjectRoomAvailabilityInfo();
  room.roomKey = dto.roomKey ?? '';
  room.kind = dto.kind ?? 0;
  room.availability = room.roomKey.length === 0 ? 'notSeeded' : (dto.availability as ProjectRoomAvailability);
  // A value this client cannot name is dropped rather than kept as a raw string: the room would
  // otherwise be offered a decision route it cannot draw a control for.
  room.actions = (dto.actions ?? [])
    .map(parseProjectDecisionWord)
    .filter((action): action is ProjectDecisionWord => action !== undefined);
  room.supportsEditDescription = dto.supportsEditDescription ?? false;
  // Undefined when the project anchors no such room (the API leaves it out).
  room.deliverableTaskId = dto.deliverableTaskId ?? undefined;
  room.name = dto.name;
  room.milestoneTaskId = dto.milestoneTaskId;
  room.sortOrder = dto.sortOrder ?? 0;
  room.confirmed = dto.confirmed ?? false;
  // A moment: the instant the server ticked the anchor task, like every other stamp on this read.
  room.confirmedDate = fromApiDateTimeOptional(dto.confirmedDate);
  room.deadline = fromApiDateTimeOptional(dto.deadline);
  room.confirmable = dto.confirmable;
  room.readyToConfirm = dto.readyToConfirm ?? false;
  room.done = dto.done ?? false;
  room.started = dto.started ?? false;
  if (dto.progress !== undefined && dto.progress !== null) {
    room.progress = new ProjectRoomProgress();
    room.progress.done = dto.progress.done ?? 0;
    room.progress.total = dto.progress.total ?? 0;
  }

  return room;
}

/** The members the list read and every project read share. */
function mapSummaryMembers(dto: ProjectSummaryDTO, project: ProjectSummary): void {
  project.projectId = dto.projectId ?? 0;
  project.name = dto.name ?? '';
  project.description = dto.description ?? '';
  project.state = (dto.state ?? ProjectState.active) as ProjectState;
  project.isCompleted = dto.isCompleted ?? false;
  // Normalised, like every other optional wire fact: the API leaves a null out, and undefined here
  // means this project has no package.
  project.packageId = dto.packageId ?? undefined;
  project.canDelete = dto.canDelete ?? true;
  project.authSchemaId = dto.authSchemaId ?? undefined;
  project.normIds = [...(dto.normIds ?? [])];
  project.tagIds = dto.tagIds?.idList?.map<number>((tagId) => Number(tagId));
  // A date, not a moment: read through the date-only helper so a target of the 1st does not become
  // the 31st of the month before in a negative time zone offset.
  project.targetDate = fromApiDateOptional(dto.targetDate);
  project.created = fromApiDateTimeOptional(dto.created);
}

/** One row of the list read (GET projects). */
export function mapFromProjectSummary(dto: ProjectSummaryDTO): ProjectSummary {
  const project = new ProjectSummary();
  mapSummaryMembers(dto, project);

  return project;
}

/**
 * Builds the domain project.
 *
 * `hasDetail` is the caller's statement about which route was called, not something derived from
 * the DTO: an empty `milestones` looks identical on a mutation response and on a detail read of a
 * project that anchors none, and only the caller knows which request it made. Pass true for GET
 * projects/{id} and the POST projects 201; false for the state and edit responses.
 */
export function mapFromProject(dto: ProjectDTO, hasDetail: boolean, cache?: GlobalDataCache): Project {
  const project = new Project(hasDetail);
  mapSummaryMembers(dto, project);
  project.tier = dto.tier === undefined || dto.tier === null ? undefined : mapFromProjectTier(dto.tier);
  project.milestones = (dto.milestones ?? []).map(mapFromProjectMilestone);
  project.rooms = (dto.rooms ?? []).map(mapFromProjectRoomAvailability);
  // A moment: the day work started is an instant the server stamped, unlike the target date the
  // user picked.
  project.startDate = fromApiDateTimeOptional(dto.startDate);
  // Undefined survives as undefined. Only the single read fills the track; on every other read it
  // means not loaded, and a timeline drawn from a fabricated empty track would show a forecast that
  // was never made.
  project.track = dto.track === undefined || dto.track === null ? undefined : mapFromProjectTrack(dto.track);
  if (dto.dashboard && cache) {
    project.dashboard = mapFromDashboard(dto.dashboard, cache);
  }
  project.externalAudit = dto.externalAudit ? mapFromProjectExternalAudit(dto.externalAudit) : undefined;
  project.writeBlocker = parseProjectWriteBlocker(dto.writeBlocker);

  return project;
}

/** The list read: summaries only, without milestones, rooms, tier or track. */
export function mapFromProjects(dtos: ProjectSummaryDTO[] | undefined): ProjectSummary[] {
  return (dtos ?? []).map(mapFromProjectSummary);
}

export function mapFromKickstartOffer(offerDto: KickstartOfferDTO): KickstartOffer {
  const offer = new KickstartOffer();
  offer.packageId = offerDto.packageId ?? undefined;
  offer.trialPackageId = offerDto.trialPackageId ?? undefined;

  return offer;
}

export function mapFromProjectTier(dto: ProjectTierDTO): ProjectTierInfo {
  const tier = new ProjectTierInfo();
  tier.tier = (dto.tier ?? 'trial') as ProjectTier;
  tier.writesOpen = dto.writesOpen ?? false;
  tier.trialExpired = dto.trialExpired ?? false;
  // The package facts. Nothing is invented: a 0 here would let a screen claim this project runs on
  // package zero, but every one of them is normalised to `undefined`, because the wire sends JSON
  // `null` for a fact the server has no answer for, and `null` is not `undefined`. A screen testing
  // `!== undefined` would treat a null as a real answer and render an empty half of a sentence; the
  // preview package's absent trial clock (`trialDaysLeft: null`) is that case.
  tier.packageId = dto.packageId ?? undefined;
  tier.packageName = dto.packageName ?? undefined;
  tier.packageState = dto.packageState ?? undefined;
  tier.trialDaysLeft = dto.trialDaysLeft ?? undefined;
  tier.upgradePackageId = dto.upgradePackageId ?? undefined;
  tier.upgradePackageName = dto.upgradePackageName ?? undefined;
  tier.upgradePrice = dto.upgradePrice ?? undefined;
  // The newer version. A null `updatePackageState` means this organization holds no row for that
  // version, so it stays undefined rather than becoming a state number.
  tier.updatePackageId = dto.updatePackageId ?? undefined;
  tier.updatePackageName = dto.updatePackageName ?? undefined;
  tier.updatePackageVersion = dto.updatePackageVersion ?? undefined;
  tier.updatePackageState = dto.updatePackageState ?? undefined;

  return tier;
}

/**
 * The lifecycle move and the target date, on one body. Omitting `expectedState` skips the
 * optimistic guard (last-writer-wins).
 *
 * `state` is optional so a body that only moves the target date leaves the lifecycle alone; an
 * omitted state reaches the API as no state at all rather than as Active. Clearing the target is
 * `clearTargetDate`, never an absent `targetDate` - absent means "this body does not name one".
 */
export function mapToProjectStateRequest(
  state: number | undefined,
  expectedState: number | undefined,
  targetDate?: Date,
  clearTargetDate?: boolean,
): ProjectStateRequestDTO {
  const dto = new ProjectStateRequestDTO();
  dto.state = state;
  dto.expectedState = expectedState;
  // A day on a calendar, sent with the time zeroed - the same helper the create body uses, so the
  // local midnight the picker produced is not shifted a day by the UTC conversion.
  dto.targetDate = toApiDateOptional(targetDate);
  dto.clearTargetDate = clearTargetDate ?? false;

  return dto;
}

/**
 * The hypothetical target a forecast preview is measured against. Clearing is `clearTargetDate`,
 * never an absent `targetDate`.
 */
export function mapToProjectTrackPreviewRequest(
  targetDate: Date | undefined,
  clearTargetDate: boolean,
): ProjectTrackPreviewRequestDTO {
  const dto = new ProjectTrackPreviewRequestDTO();
  dto.targetDate = toApiDateOptional(targetDate);
  dto.clearTargetDate = clearTargetDate;

  return dto;
}

//
// Room and apply mappers.
//
// Same discipline as above: nothing here invents a fact the wire did not send. Absent optional
// fields are copied through as undefined, because coalescing an absent value would turn "nothing
// was recorded" into a claim.
//

/**
 * Narrows an apply result row's outcome value.
 *
 * An unrecognized value resolves to `failed`: a result row this client cannot parse must not be
 * presented as a write that succeeded. It appears in the not-saved group, where the row's own
 * `code` is shown, rather than silently counted as applied. The headline counts come from the
 * result itself and are never recomputed from this.
 */
const toApplyOutcome = (value: string | undefined): ProjectApplyOutcome => {
  const word = projectApplyOutcomes.find((outcome) => outcome === value);

  return word ?? 'failed';
};

/**
 * Narrows the risks room's `method` to the RiskMethod enum the client knows - mitigate 0 | avoid
 * 1 | transfer 2 | accept 3. Null (every other room, or a risk with no method chosen) and a number
 * outside that closed set both resolve to undefined: nothing here invents a treatment value the
 * wire did not send.
 */
const toRiskMethod = (value: number | null | undefined): RiskMethod | undefined => {
  if (value === null || value === undefined) return undefined;

  const isKnownMethod: boolean =
    value === RiskMethod.Mitigate ||
    value === RiskMethod.Avoid ||
    value === RiskMethod.Transfer ||
    value === RiskMethod.Accept;

  return isKnownMethod ? (value as RiskMethod) : undefined;
};

/** One group of a role's list in the send room, with the bedrijfsmiddelen of the list in it. */
function mapFromProjectRoomAssetListGroup(dto: ProjectRoomAssetListGroupDTO): ProjectRoomAssetListGroup {
  return {
    containerId: dto.containerId ?? undefined,
    name: dto.name ?? undefined,
    assetCount: dto.assetCount,
    assets: (dto.assets ?? []).map(
      (asset: ProjectRoomInquiryAssetDTO): ProjectRoomInquiryAsset => ({
        assetId: asset.assetId,
        name: asset.name ?? '',
      }),
    ),
  };
}

/** One asset inquiry the asset register waits on. */
function mapFromProjectRoomRegisterWait(dto: ProjectRoomRegisterWaitDTO): ProjectRoomRegisterWait {
  return {
    roleName: dto.roleName ?? '',
    sent: dto.sent === true,
    person: mapFromPersonRef(dto.person),
    deadline: fromApiDateTimeOptional(dto.deadline ?? undefined),
  };
}

/** A person in a room read; undefined without one. The name is trimmed; a caller treats an empty one as none. */
function mapFromPersonRef(dto: PersonRefDTO | null | undefined): PersonRef | undefined {
  if (dto === undefined || dto === null) return undefined;

  return { userId: dto.userId ?? undefined, name: (dto.name ?? '').trim() };
}

/** One role's asset inquiry, the same in every room that names it. */
function mapFromProjectInquiry(dto: ProjectInquiryDTO): ProjectInquiry {
  return {
    roleId: dto.roleId ?? '',
    roleName: dto.roleName ?? '',
    taskId: dto.taskId ?? undefined,
    // The server always sends the status; an inquiry without one is the empty inquiry of a role that has none.
    status: parseProjectInquiryStatus(dto.status) ?? 'notSent',
    assignee: mapFromPersonRef(dto.assignee),
    askedBy: mapFromPersonRef(dto.askedBy),
    sentAt: fromApiDateTimeOptional(dto.sentAt ?? undefined),
    deadline: fromApiDateTimeOptional(dto.deadline ?? undefined),
    completedAt: fromApiDateTimeOptional(dto.completedAt ?? undefined),
    lineCount: dto.lineCount ?? 0,
    answeredCount: dto.answeredCount ?? 0,
    unansweredCount: dto.unansweredCount ?? 0,
    assigneeIsHolder: dto.assigneeIsHolder !== false,
  };
}

const parseOutcomeAnswer = (value: string | null | undefined): ProjectOutcomeAnswer | undefined =>
  parseProjectOneOf(projectOutcomeAnswers, value);

/** The controls whose questions nobody answers, and why; a control without a name shows its code. */
const mapUnownedControls = (dtos: ProjectRoomUnownedControlDTO[] | null | undefined): ProjectUnownedControl[] =>
  (dtos ?? []).map((dto) => ({
    code: dto.code ?? '',
    name: dto.name || dto.code || '',
    reason: parseProjectOneOf(projectUnownedControlReasons, dto.reason),
  }));

function mapFromQuestionRowPart(dto: ProjectQuestionRowPartDTO): ProjectQuestionRowPart {
  return {
    $kind: 'question',
    code: dto.code ?? '',
    inquiryTaskId: dto.inquiryTaskId ?? undefined,
    documentItemId: dto.documentItemId ?? undefined,
    auditCriterionId: dto.auditCriterionId ?? 0,
    commentTrailId: dto.commentTrailId ?? 0,
    auditTrailId: dto.auditTrailId ?? 0,
    state: dto.state ?? undefined,
    mode: dto.mode ?? undefined,
    taskId: dto.taskId ?? undefined,
    taskClosed: dto.taskClosed === true,
    previousAnswer:
      dto.previousAnswer === undefined || dto.previousAnswer === null
        ? undefined
        : {
            answer: parseOutcomeAnswer(dto.previousAnswer.answer),
            at: fromApiDateTimeOptional(dto.previousAnswer.at ?? undefined),
          },
    commentCount: dto.commentCount ?? 0,
    answers: (dto.answers ?? [])
      .map(parseOutcomeAnswer)
      .filter((answer): answer is ProjectOutcomeAnswer => answer !== undefined),
    answer:
      dto.answer === undefined || dto.answer === null ? undefined : { answer: parseOutcomeAnswer(dto.answer.answer) },
    complete: dto.complete === true,
    notApplicableHere: dto.notApplicableHere === true,
    evidence: (dto.evidence ?? []).map((file) => ({
      linkId: file.linkId ?? 0,
      name: file.name ?? '',
      url: file.url ?? undefined,
      evidenceTaskId: file.evidenceTaskId ?? undefined,
    })),
  };
}

function mapFromDocumentRowPart(dto: ProjectDocumentRowPartDTO): ProjectDocumentRowPart {
  return {
    $kind: 'document',
    linkId: dto.linkId ?? undefined,
    inquiryTaskId: dto.inquiryTaskId ?? undefined,
    owner:
      dto.owner === undefined || dto.owner === null
        ? undefined
        : {
            roleName: dto.owner.roleName ?? undefined,
            people: (dto.owner.people ?? [])
              .map(mapFromPersonRef)
              .filter((person): person is PersonRef => person !== undefined),
          },
    choosable: dto.choosable === true,
    choice: parseProjectOneOf(projectDocumentChoices, dto.choice),
    reason: dto.reason ?? undefined,
    complete: dto.complete === true,
  };
}

function mapFromOwnedItem(dto: ProjectRoomOwnedItemDTO): ProjectRoomOwnedItem {
  return {
    id: dto.id ?? 0,
    code: dto.code ?? undefined,
    name: dto.name ?? '',
  };
}

function mapFromOwnershipRowPart(dto: ProjectOwnershipRowPartDTO): ProjectOwnershipRowPart {
  return {
    $kind: 'ownership',
    roleId: dto.roleId ?? '',
    roleName: dto.roleName ?? '',
    inquiryTaskId: dto.inquiryTaskId ?? undefined,
    controls: (dto.controls ?? []).map(mapFromOwnedItem),
    risks: (dto.risks ?? []).map(mapFromOwnedItem),
    documents: (dto.documents ?? []).map(mapFromOwnedItem),
  };
}

/** One message of an inquiry's responsible person. */
function mapFromProjectRoomInquirySignal(dto: ProjectRoomInquirySignalDTO): ProjectRoomInquirySignal {
  return {
    created: fromApiDateTimeRequired(dto.created),
    authorId: dto.authorId ?? undefined,
    text: commentToText(dto.text),
    entryId: dto.entryId ?? undefined,
  };
}

/** A row of the inquiry progress room: the person and each inquiry with what came back. */
function mapFromInquiryProgressRowPart(dto: ProjectInquiryProgressRowPartDTO): ProjectInquiryProgressRowPart {
  return {
    $kind: 'inquiryProgress',
    person: mapFromPersonRef(dto.person),
    inquiries: (dto.inquiries ?? []).map((progress) => ({
      roomKey: progress.roomKey ?? '',
      inquiry: mapFromProjectInquiry(progress.inquiry ?? {}),
      blocker: parseProjectInquiryBlocker(progress.blocker),
      messages: (progress.messages ?? []).map(mapFromProjectRoomInquirySignal),
      discuss: (progress.discuss ?? []).map((document) => ({
        linkId: document.linkId ?? 0,
        name: document.name ?? '',
        reason: document.reason ?? undefined,
      })),
      unsure: (progress.unsure ?? []).map((question) => ({
        code: question.code ?? '',
        name: question.name ?? '',
      })),
    })),
  };
}

/**
 * The compiler's check that a switch over a part's `$kind` handles every kind: `dto` is `never` once every kind has a
 * case. At run time a kind this build does not know (a newer API) gives `fallback`.
 */
const assertNeverKind = <T>(dto: never, fallback: T): T => {
  void dto;

  return fallback;
};

/** A group of an asset inquiry's list as the window shows it. */
function mapFromProjectInquiryListGroup(group: ProjectInquiryListGroupDTO): ProjectInquiryListGroup {
  // The help of the group as plain text; an empty description is none.
  const description = htmlToText(group.description ?? '', false).trim();

  return {
    containerId: group.containerId ?? undefined,
    name: group.name ?? undefined,
    description: description.length > 0 ? description : undefined,
    assetType: group.assetType ?? 0,
    itemIds: group.itemIds ?? [],
    status: parseProjectInquiryGroupStatus(group.status),
    containerOwnedByRole: group.containerOwnedByRole === true,
    commentTrailId: group.commentTrailId ?? 0,
    auditTrailId: group.auditTrailId ?? 0,
  };
}

/** The document groups of an outcome list. */
const mapFromProjectOutcomeGroups = (groups: ProjectOutcomeGroupDTO[] | null | undefined): ProjectOutcomeGroup[] =>
  (groups ?? []).map((group) => ({
    documentItemId: group.documentItemId ?? '',
    itemIds: group.itemIds ?? [],
  }));

/** What is still open in a room, each counted; a fact without a count is left out. */
const mapFromProjectRoomTodos = (dtos: ProjectRoomTodoDTO[] | null | undefined): ProjectRoomTodo[] =>
  (dtos ?? []).filter((fact) => fact.count > 0).map((fact) => new ProjectRoomTodo(fact.key, fact.count));

/** What a send promises. */
const mapFromProjectInquirySendTerms = (
  dto: ProjectInquirySendTermsDTO | null | undefined,
): ProjectInquirySendTerms => ({
  responseDays: dto?.responseDays ?? 0,
  deadline: fromApiDateTimeRequired(dto?.deadline ?? ''),
});

/**
 * What the documents room knows about who approves its documents; an approve mode this client cannot name is `none`,
 * and an approve block it cannot name is `notAllowed`.
 */
const mapFromProjectDocumentsRoomPart = (dto: ProjectDocumentsRoomPartDTO): ProjectDocumentsRoomPart => ({
  $kind: 'documentsRoom',
  approveMode: parseProjectOneOf(projectKickoffApproveModes, dto.approveMode) ?? 'none',
  approveBlock:
    dto.approveBlock === undefined || dto.approveBlock === null
      ? undefined
      : parseProjectOneOf(projectKickoffApproveBlocks, dto.approveBlock) ?? 'notAllowed',
  directieUserId: dto.directieUserId ?? undefined,
  directieName: dto.directieName ?? undefined,
  directieRoleName: dto.directieRoleName ?? undefined,
});

/** The part of a room read, of its kind; undefined without one, and for a kind this build does not know. */
function mapFromProjectRoomPart(dto: ProjectRoomPartDTO | null | undefined): ProjectRoomPart | undefined {
  if (dto === undefined || dto === null) return undefined;

  switch (dto.$kind) {
    case 'risks':
      return dto.precheck ? { $kind: 'risks', precheck: mapFromProjectRiskPrecheck(dto.precheck) } : undefined;
    case 'scope':
      return { $kind: 'scope', documentsComplete: dto.documentsComplete === true };
    case 'documentsRoom':
      return mapFromProjectDocumentsRoomPart(dto);
    case 'assetInquiry':
      return {
        $kind: 'assetInquiry',
        callerMayReassign: dto.callerMayReassign === true,
        send: mapFromProjectInquirySendTerms(dto.send),
        todo: mapFromProjectRoomTodos(dto.todo),
      };
    case 'outcomeInquiry':
      return {
        $kind: 'outcomeInquiry',
        callerMayReassign: dto.callerMayReassign === true,
        send: mapFromProjectInquirySendTerms(dto.send),
        todo: mapFromProjectRoomTodos(dto.todo),
        hasCatalogue: dto.hasCatalogue === true,
        unownedControls: mapUnownedControls(dto.unownedControls),
      };
    case 'assetAnswers':
      return {
        $kind: 'assetAnswers',
        callerMayReassign: dto.callerMayReassign === true,
        registerApprovedAt: fromApiDateTimeOptional(dto.registerApprovedAt ?? undefined),
        inquiries: (dto.inquiries ?? []).map(mapFromProjectInquiry),
        lists: (dto.lists ?? []).map((list) => ({
          taskId: list.taskId ?? undefined,
          mode: parseProjectInquiryListMode(list.mode),
          signals: (list.signals ?? []).map(mapFromProjectRoomInquirySignal),
          groups: (list.groups ?? []).map(mapFromProjectInquiryListGroup),
          outcomeInquirySent: list.outcomeInquirySent === true,
        })),
      };
    case 'outcomeAnswers':
      return {
        $kind: 'outcomeAnswers',
        callerMayReassign: dto.callerMayReassign === true,
        inquiries: (dto.inquiries ?? []).map(mapFromProjectInquiry),
        lists: (dto.lists ?? []).map((list) => ({
          taskId: list.taskId ?? undefined,
          roleId: list.roleId ?? '',
          writable: list.writable,
          signals: (list.signals ?? []).map(mapFromProjectRoomInquirySignal),
          groups: mapFromProjectOutcomeGroups(list.groups),
          ownershipItemId: list.ownershipItemId ?? undefined,
        })),
      };
    case 'inquiryProgress':
      return {
        $kind: 'inquiryProgress',
        callerMayReassign: dto.callerMayReassign === true,
        todo: mapFromProjectRoomTodos(dto.todo),
      };
    case 'externalAudit':
      return { $kind: 'externalAudit', state: mapFromProjectExternalAuditRoomPart(dto) };
    case 'assetRegister':
      return {
        $kind: 'assetRegister',
        approvedAt: fromApiDateTimeOptional(dto.approvedAt ?? undefined),
        inquiries: (dto.inquiries ?? []).map(mapFromProjectInquiry),
        waitingOn: (dto.waitingOn ?? []).map(mapFromProjectRoomRegisterWait),
        lockedUntil: fromApiDateTimeOptional(dto.lockedUntil ?? undefined),
        toAssign: dto.toAssign ?? 0,
        canApprove: dto.canApprove === true,
        approveBlockers: (dto.approveBlockers ?? [])
          .map((blocker) => parseProjectOneOf(projectRegisterApproveBlockers, blocker))
          .filter((blocker): blocker is ProjectRegisterApproveBlocker => blocker !== undefined),
        duplicates: mapFromProjectAssetDuplicates(dto.duplicates),
        ownerRoleIds: dto.ownerRoleIds ?? [],
      };
    default:
      return assertNeverKind(dto, undefined);
  }
}

/**
 * The duplicate search of the asset register; a proposal of a status this client cannot name is left out, and so is a
 * failure of a reason it cannot name.
 */
function mapFromProjectAssetDuplicates(dto: ProjectAssetDuplicatesDTO | null | undefined): ProjectAssetDuplicates {
  const proposals = (dto?.proposals ?? []).flatMap((proposal): ProjectAssetDuplicateProposal[] => {
    const status: ProjectAssetDuplicateProposalStatus | undefined = parseProjectOneOf(
      projectAssetDuplicateProposalStatuses,
      proposal.status,
    );
    const itemId = proposal.itemId ?? '';
    if (status === undefined || !itemId.startsWith(projectDuplicatesItemPrefix)) return [];

    return [
      {
        itemId: itemId,
        proposalId: itemId.substring(projectDuplicatesItemPrefix.length),
        status: status,
        members: (proposal.members ?? []).map((member) => ({
          itemId: member.itemId ?? '',
          linked: member.linked === true,
          fromPackage: member.fromPackage === true,
          hasExternalId: member.hasExternalId === true,
          outOfUse: mapFromProjectAssetAnswer(member.outOfUse),
          outOfUseRoleName: member.outOfUseRoleName ?? undefined,
        })),
        staysItemId: proposal.staysItemId ?? undefined,
        blocked: proposal.blocked === true,
        stale: proposal.stale === true,
        suggestedNameItemId: proposal.suggestedNameItemId ?? undefined,
        reason: proposal.reason ?? undefined,
        resolvedBy: proposal.resolvedBy ? mapFromPersonRef(proposal.resolvedBy) : undefined,
        resolvedOn: fromApiDateTimeOptional(proposal.resolvedOn ?? undefined),
      },
    ];
  });

  const failureReason = parseProjectOneOf(projectAssetDuplicateFailures, dto?.failure?.reason);

  return {
    available: dto?.available === true,
    addOnMissing: dto?.addOnMissing === true,
    checked: dto?.checked === true,
    failure:
      failureReason === undefined
        ? undefined
        : {
            reason: failureReason,
            inUseCount: dto?.failure?.inUseCount ?? 0,
            outOfUseCount: dto?.failure?.outOfUseCount ?? 0,
            max: dto?.failure?.max ?? 0,
          },
    openCount: dto?.openCount ?? 0,
    proposals: proposals,
  };
}

/** The item id of a proposal of the asset register's duplicate search: `duplicates:{proposalId}`. */
const projectDuplicatesItemPrefix = 'duplicates:';

/** The answer on an asset line, of its kind; undefined without one, and for a kind this build does not know. */
function mapFromProjectAssetAnswer(dto: ProjectAssetAnswerDTO | null | undefined): ProjectAssetLineAnswer | undefined {
  if (dto === undefined || dto === null) return undefined;

  const by = mapFromPersonRef(dto.by);
  const on = fromApiDateTimeOptional(dto.on ?? undefined);
  switch (dto.$kind) {
    case 'keep':
      return { $kind: 'keep', by: by, on: on };
    case 'adjust':
      return {
        $kind: 'adjust',
        by: by,
        on: on,
        previousName: dto.previousName ?? undefined,
        previousContainerId: dto.previousContainerId ?? undefined,
      };
    case 'notInUse':
      return { $kind: 'notInUse', by: by, on: on, reason: parseProjectOneOf(projectAssetArchiveReasons, dto.reason) };
    case 'added':
      return { $kind: 'added', by: by, on: on };
    default:
      return assertNeverKind(dto, undefined);
  }
}

/** The holders of a role. */
const mapFromProjectRoomHolders = (dtos: ProjectRoomHolderDTO[] | null | undefined): ProjectRoomHolder[] =>
  (dtos ?? []).map((holder) => ({
    userId: holder.userId ?? undefined,
    name: holder.name ?? '',
    licenceState: parseProjectRoomHolderLicenceState(holder.licenceState ?? undefined),
  }));

/** The part of a row, of its kind; undefined without one, and for a kind this build does not know. */
function mapFromProjectRoomItemPart(dto: ProjectRoomItemPartDTO | null | undefined): ProjectRoomItemPart | undefined {
  if (dto === undefined || dto === null) return undefined;

  switch (dto.$kind) {
    case 'role':
      return { $kind: 'role', holders: mapFromProjectRoomHolders(dto.holders) };
    case 'risk':
      // Nothing invented: an absent text stays empty, an absent method undefined.
      return {
        $kind: 'risk',
        asset: dto.asset ?? '',
        analysis: dto.analysis ?? '',
        strategy: dto.strategy ?? '',
        method: toRiskMethod(dto.method),
        controls: (dto.controls ?? []).map(
          (control: ProjectRoomRelatedDTO): ProjectRoomRelated => ({
            id: control.id ?? '',
            code: control.code ?? '',
            name: control.name ?? '',
          }),
        ),
      };
    case 'assetInquiryRole':
      return {
        $kind: 'assetInquiryRole',
        holders: mapFromProjectRoomHolders(dto.holders),
        inquiry: mapFromProjectInquiry(dto.inquiry ?? {}),
        blocker: parseProjectInquiryBlocker(dto.blocker),
        sendTo: mapFromPersonRef(dto.sendTo),
        assetCount: dto.assetCount,
        groups: (dto.groups ?? []).map(mapFromProjectRoomAssetListGroup),
      };
    case 'outcomeInquiryRole':
      return {
        $kind: 'outcomeInquiryRole',
        holders: mapFromProjectRoomHolders(dto.holders),
        inquiry: mapFromProjectInquiry(dto.inquiry ?? {}),
        blocker: parseProjectInquiryBlocker(dto.blocker),
        sendTo: mapFromPersonRef(dto.sendTo),
      };
    case 'answerLine':
      return {
        $kind: 'answerLine',
        containerId: dto.containerId ?? undefined,
        ownerRoleId: dto.ownerRoleId ?? undefined,
        inquiryTaskId: dto.inquiryTaskId,
        answer: mapFromProjectAssetAnswer(dto.answer),
        removable: dto.removable === true,
        editable: dto.editable === true,
        lateAddition: dto.lateAddition === true,
      };
    case 'previewLine':
      return {
        $kind: 'previewLine',
        containerId: dto.containerId ?? undefined,
        ownerRoleId: dto.ownerRoleId ?? undefined,
      };
    case 'registerLine':
      return {
        $kind: 'registerLine',
        containerId: dto.containerId ?? undefined,
        ownerRoleId: dto.ownerRoleId ?? undefined,
        answer: mapFromProjectAssetAnswer(dto.answer),
        lateAddition: dto.lateAddition === true,
        projectRoleId: dto.projectRoleId ?? undefined,
        projectRoleName: dto.projectRoleName ?? undefined,
        inquiryTaskId: dto.inquiryTaskId ?? undefined,
        unanswered: dto.unanswered === true,
        toAssign: dto.toAssign === true,
        ownedGroup: dto.ownedGroup === true,
        assetType: dto.assetType ?? 0,
        addedWithoutGroup: dto.addedWithoutGroup === true,
        added:
          dto.added === undefined || dto.added === null
            ? undefined
            : { by: mapFromPersonRef(dto.added.by), at: fromApiDateTimeOptional(dto.added.at ?? undefined) },
        duplicateProposalId: dto.duplicateProposalId ?? undefined,
        mergedInto:
          dto.mergedInto === undefined || dto.mergedInto === null
            ? undefined
            : {
                itemId: dto.mergedInto.itemId ?? '',
                name: dto.mergedInto.name ?? undefined,
                mergedOn: fromApiDateTimeOptional(dto.mergedInto.mergedOn ?? undefined),
                mergedBy: dto.mergedInto.mergedBy ? mapFromPersonRef(dto.mergedInto.mergedBy) : undefined,
                unmergeBlocker: parseProjectOneOf(projectRegisterMergeRefusals, dto.mergedInto.unmergeBlocker),
              },
      };
    case 'question':
      return mapFromQuestionRowPart(dto);
    case 'document':
      return mapFromDocumentRowPart(dto);
    case 'ownership':
      return mapFromOwnershipRowPart(dto);
    case 'inquiryProgress':
      return mapFromInquiryProgressRowPart(dto);
    case 'kickoffDocument':
      return {
        $kind: 'kickoffDocument',
        recordedById: dto.recordedById ?? undefined,
      };
    default:
      return assertNeverKind(dto, undefined);
  }
}

function mapFromProjectRoomItem(dto: ProjectRoomItemDTO): ProjectRoomItem {
  const item = new ProjectRoomItem();
  item.itemId = dto.itemId ?? '';
  item.code = dto.code ?? '';
  item.externalId = dto.externalId ?? undefined;
  item.name = dto.name ?? '';
  item.description = dto.description ?? '';
  // The record's own state column; undefined for a kind without one (roles, the inquiry rows).
  item.state = dto.state ?? undefined;
  // A confirmed room states no per-item decision on the wire, so this is what says a row was set
  // aside - see projectRoomDTO.ts's header on `isArchived`.
  item.isArchived = dto.isArchived === true;
  item.unresolved = dto.unresolved === true;
  item.normIds = dto.normIds ?? [];
  item.ownerId = dto.ownerId ?? undefined;
  item.stateName = dto.stateName ?? undefined;
  item.tagIds = dto.tagIds ?? [];
  item.part = mapFromProjectRoomItemPart(dto.part);

  return item;
}

export function mapFromProjectRoom(dto: ProjectRoomDTO): ProjectRoom {
  const room = new ProjectRoom();
  room.projectId = dto.projectId ?? 0;
  room.roomKey = dto.roomKey ?? '';
  room.kind = dto.kind ?? 0;
  room.actions = (dto.actions ?? [])
    .map(parseProjectDecisionWord)
    .filter((action): action is ProjectDecisionWord => action !== undefined);
  // Undefined when the project anchors no such room (the API leaves it out).
  room.deliverableTaskId = dto.deliverableTaskId ?? undefined;
  room.part = mapFromProjectRoomPart(dto.part);
  room.writeBlocker = parseProjectWriteBlocker(dto.writeBlocker);
  room.items = (dto.items ?? []).map(mapFromProjectRoomItem);

  return room;
}

function mapFromProjectApplyResultItem(dto: ProjectApplyItemResultDTO): ProjectApplyResultItem {
  const item = new ProjectApplyResultItem();
  item.ref = dto.ref ?? '';
  // A value this client cannot name stays undefined rather than becoming a string that cannot
  // render.
  item.action = parseProjectDecisionWord(dto.action);
  item.outcome = toApplyOutcome(dto.outcome);
  item.code = dto.code;
  item.reason = dto.reason;
  item.createdTaskId = dto.createdTaskId;
  item.sameNameItemId = dto.sameNameItemId ?? undefined;
  item.refusalReason = parseProjectOneOf(projectRegisterMergeRefusals, dto.refusalReason);
  item.warning = parseProjectOneOf(projectApplyItemWarnings, dto.warning);
  item.createdAssessments = dto.createdAssessments?.map(mapFromAssessmentTaskCreatedDTO);
  item.changed = dto.changed
    ? {
        name: dto.changed.name ?? '',
        modifiedOn: fromApiDateTimeOptional(dto.changed.modifiedOn ?? undefined),
        changedBy: mapFromPersonRef(dto.changed.changedBy),
      }
    : undefined;

  return item;
}

/**
 * The apply response. Nothing is carried through beyond the direct outcome: no id, no key, no
 * replay flag, no version token. `roomConfirmed` and `advanceReason` let the room state "this apply
 * wrote but did not confirm" instead of the caller guessing from the counts alone.
 */
export function mapFromProjectApplyResult(dto: ProjectApplyResultDTO): ProjectApplyResult {
  const result = new ProjectApplyResult();
  result.appliedCount = dto.appliedCount ?? 0;
  result.alreadyAppliedCount = dto.alreadyAppliedCount ?? 0;
  result.failedCount = dto.failedCount ?? 0;
  result.items = (dto.items ?? []).map(mapFromProjectApplyResultItem);
  result.roomConfirmed = dto.roomConfirmed ?? false;
  result.advanceReason = dto.advanceReason;
  result.changedItems = (dto.changedItems ?? []).map(mapFromProjectRoomItem);
  result.changedGroups = new Map(
    Object.entries(dto.changedGroups ?? {}).map(([itemId, status]) => [itemId, parseProjectInquiryGroupStatus(status)]),
  );
  result.stamp = fromApiDateTimeOptional(dto.stamp ?? undefined);

  return result;
}

function mapFromProjectRiskPrecheck(dto: ProjectRiskPrecheckDTO): ProjectRiskPrecheck {
  const precheck = new ProjectRiskPrecheck();
  precheck.riskStatesConfigured = dto.riskStatesConfigured ?? false;
  precheck.riskStateCount = dto.riskStateCount ?? 0;
  precheck.archivedRiskStateId = dto.archivedRiskStateId;
  precheck.archivedStateAvailable = dto.archivedStateAvailable ?? false;

  return precheck;
}

/**
 * The apply envelope. It assembles the envelope only - which fields each action may carry, and what
 * is stripped, belongs to the apply payload builder, which emits these DTOs directly; a second
 * per-decision mapping hop here could let a stripped field come back and turn a whole batch into a
 * 400.
 *
 * No key, no token: a retry is a plain re-POST of whichever decisions remain to send, and the
 * server tells the caller which those are through the response's own outcome list. `complete` is
 * the one flag beside the batch: false saves and leaves the deliverable open, true saves and
 * completes it. `connectionId` is the caller's progress hub connection, for a room that reports progress.
 */
export function mapToProjectApplyRequest(
  decisions: ProjectApplyDecisionDTO[],
  complete: boolean = false,
  connectionId?: string,
): ProjectApplyRequestDTO {
  const dto = new ProjectApplyRequestDTO();
  dto.decisions = [...decisions];
  dto.complete = complete;
  dto.connectionId = connectionId;

  return dto;
}
